import {
  Directive,
  ElementRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  PROBATE_SUBSCRIBER_APP_STEP_NAVIGATION,
  PROBATE_CLERK_APP_STEP_NAVIGATION,
} from '@app/core/probate/constants/navigation.constants';
import { TenantStepNavigation } from '@app/core/models/tenant-step-navigation.model';
import { AuthService } from '@app/core/services/auth/auth.service';
import { GenericApplicationSections } from '@shared/interfaces/agnostic-sections.type';

@Directive({
  selector: '[appClickAndNavigate]',
  standalone: true,
})
export class ClickAndNavigateDirective {
  private elementRef = inject(ElementRef);
  private router = inject(Router);
  private authService = inject(AuthService);
  private activatedRoute = inject(ActivatedRoute);

  // Input signal for the target section
  targetSection = input.required<GenericApplicationSections>();

  // Input signal for the original click handler (optional)
  originalHandler = input<() => void | Promise<unknown>>();

  // Output signal for when navigation occurs
  navigate = output<GenericApplicationSections>();

  // Output signal for when an error occurs
  errorOccurred = output<Error>();

  // Internal signal to track if navigation is in progress
  private isNavigating = signal(false);

  constructor() {
    this.setupClickHandler();
  }

  private setupClickHandler(): void {
    const element = this.elementRef.nativeElement;

    if (element) {
      element.addEventListener('click', this.handleClick.bind(this));
    }
  }

  private async handleClick(event: MouseEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    if (this.isNavigating()) {
      return;
    }

    this.isNavigating.set(true);

    try {
      const handler = this.originalHandler();
      if (handler) {
        await handler();
      }

      const section = this.targetSection();
      const navigationMap = this.getNavigationMap();
      const route = navigationMap[section];

      if (route?.NEXT_STEP_ROUTE) {
        await this.router.navigate([route.NEXT_STEP_ROUTE], {
          queryParamsHandling: 'merge',
          preserveFragment: true,
          relativeTo: this.activatedRoute,
        });
        this.navigate.emit(section);
      } else {
        throw new Error(`No route found for section: ${section}`);
      }
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      this.errorOccurred.emit(errorObj);
      console.error('Click handler failed, navigation cancelled:', errorObj);
    } finally {
      this.isNavigating.set(false);
    }
  }

  get navigating(): boolean {
    return this.isNavigating();
  }

  private getNavigationMap(): TenantStepNavigation {
    const isClerk = this.authService.isClerk();

    return isClerk
      ? PROBATE_CLERK_APP_STEP_NAVIGATION
      : PROBATE_SUBSCRIBER_APP_STEP_NAVIGATION;
  }
}
